import { DownloadButton } from './DownloadButton';
import { useDerivedNetworkContext } from '@/context';
import { buildUrl, doFetch } from '@/services/queryUtils';
import { useAuth } from '@acast-tech/auth-client';
import { useState, useEffect } from 'react';
import {
  Button,
  ActionBanner,
  ActionBannerIcon,
  ActionBannerActions,
  ActionBannerBody,
} from '@acast-tech/decibel';
import { TriangleExclamationRegularGlyph } from '@acast-tech/decibel-glyphs';
import { downloadFile } from '@/utils/dataDownloadUtils/downloadFiles';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';

const PayoutsDownloadTrigger: React.FC<{ year: string; month: string }> = ({ year, month }) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { getToken } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setErrorMessage(null);
  }, [year, month]);

  const handlePdfOpen = async () => {
    mixpanelTrack(MIXPANEL_EVENT.payouts.highlights.vendorStatementsDownloadClicked, {
      widget: RevenueDashboardWidget.PayoutSinglePageHeading,
      page: 'payouts',
    });
    setErrorMessage(null);
    setIsDownloading(true);

    try {
      if (!selectedNetwork?.id) {
        throw new Error('No network selected');
      }

      const token = await getToken();
      const path = buildUrl(`/v2/networks/${selectedNetwork.id}/payouts/vendor-statements`, {
        year,
        month,
      });

      const response = await doFetch(path, token);
      const contentType = response.headers.get('Content-Type') ?? '';

      if (contentType.includes('application/zip')) {
        const blob = await response.blob();
        const zipFileName =
          response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            ?.replace(/"/g, '')
            ?.trim() || 'vendor-statements.zip';
        downloadFile(blob, zipFileName);
        mixpanelTrack(MIXPANEL_EVENT.payouts.highlights.vendorStatementsDownloadSuccess, {
          widget: RevenueDashboardWidget.PayoutSinglePageHeading,
          page: 'payouts',
        });

        const warningMessage = response.headers.get('X-Warning-Message');
        if (warningMessage) {
          setErrorMessage(warningMessage);
        }
      } else if (contentType.includes('application/json')) {
        const data = await response.json();
        setErrorMessage(data?.message?.trim() ?? 'No statement files found');
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'An unexpected error occurred while downloading the statement PDFs'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {errorMessage && (
        <ActionBanner
          style={{
            maxWidth: '600px',
            flex: 1,
          }}
        >
          <ActionBannerIcon glyph={TriangleExclamationRegularGlyph} />
          <ActionBannerBody>{errorMessage}</ActionBannerBody>
          <ActionBannerActions>
            <Button variant="tertiary" onClick={() => setErrorMessage(null)}>
              Dismiss
            </Button>
          </ActionBannerActions>
        </ActionBanner>
      )}
      <DownloadButton
        onClick={handlePdfOpen}
        label={isDownloading ? 'Downloading ...' : 'Download PDFs'}
        disabled={isDownloading}
      />
    </div>
  );
};

export default PayoutsDownloadTrigger;
